import React, { useState, useEffect } from 'react';
import {
	CardNumberElement,
	CardExpiryElement,
	CardCvcElement,
	injectStripe,
} from 'react-stripe-elements';

import AlertModal from '../shared/AlertModal';
import LoaderModal from '../shared/LoaderModal';
import CheckoutForm from './CheckoutForm';

const inputStyle = {
	base: {
		color: 'rgba(0, 0, 0, 0.75)',
	},
};

const SubscriptionForm = ({
	plan,
	hst,
	currentSubscription,
	stripe,
	coupons,
	defaultPaymentMethod,
	scTrialAvailable,
	product,
	plans,
}) => {
	const [subscription, setSubscription] = useState({});
	const [useDefaultPaymentMethod, setUseDefaultPaymentMethod] =
		useState(false);
	const [name, setName] = useState('');
	const [coupon_id, setCoupon] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [alertMessage, setAlertMessage] = useState(null);

	useEffect(() => {
		handlePlanChange(plan);
	}, []);

	useEffect(() => {
		if (defaultPaymentMethod) {
			setUseDefaultPaymentMethod(true);
		}
	}, []);

	const showError = (msg) => {
		setAlertMessage(<div className="color-alert">{msg}</div>);
	};
	const showSuccess = (msg) => {
		setAlertMessage(<div className="color-success">{msg}</div>);
	};

	const handleDefaultPaymentMethodChange = () => {
		setUseDefaultPaymentMethod(!useDefaultPaymentMethod);
	};

	const handleCouponChange = (plan, value) => {
		setCoupon(value);

		const coupon = coupons.find(function (coupon) {
			return coupon.id == value || coupon.name == value;
		});

		if (coupon) {
			showSuccess('Promo code applied successfully');
			handlePlanChange(plan, coupon);
			// check if there is a coupon currently attached and remove it
		} else if (subscription.coupon_id != '') {
			showSuccess('Promo code removed');
			handlePlanChange(plan);
		}
	};

	const handlePlanChange = (plan, coupon) => {
		let sub_total = parseFloat(plan.amount / 100);
		let coupon_id = '';
		if (coupon) {
			if (coupon.percent_off) {
				sub_total = sub_total - (sub_total * coupon.percent_off) / 100;
			} else if (coupon.amount_off) {
				sub_total = sub_total - coupon.amount_off / 100;
			}

			coupon_id = coupon.id;
		} else {
			// if the promo was added and then changed, or if the plan was changed, then remove the coupon code from the input field (this needs a refactor, belongs elsewhere)
			setCoupon('');
		}

		const tax_amount = parseFloat(sub_total * hst);
		const total = parseFloat(sub_total + tax_amount);

		setSubscription({
			plan_id: plan.id,
			product: plan.product_kind,
			sub_total,
			tax_amount: tax_amount.toFixed(2),
			total: total.toFixed(2),
			currency: plan.currency,
			coupon_id: coupon_id,
		});
	};

	const submit = () => {
		if (!subscription.plan_id) {
			showError('Please select a plan.');
			return;
		}
		if (!name && !useDefaultPaymentMethod) {
			showError('Name on card is required.');
			return;
		}

		if (plan.kind == 'sku') {
			// charge once
			charge();
		} else {
			if (currentSubscription !== undefined) {
				// update subscription
				update();
			} else {
				// subscribe to monthly/yearly plan
				subscribe();
			}
		}
	};

	const charge = async () => {
		setIsSubmitting(true);

		let paymentMethodId = '';
		if (useDefaultPaymentMethod) {
			paymentMethodId = defaultPaymentMethod.id;
		} else {
			const { paymentMethod, error: paymentMethodError } =
				await stripe.createPaymentMethod('card', {
					billing_details: { name },
				});
			paymentMethodId = paymentMethod.id;
			if (paymentMethodError) {
				showError(paymentMethodError.message);
				setIsSubmitting(false);
				return;
			}
		}

		const body = {
			subscription: {
				...subscription,
				payment_method_id: paymentMethodId,
			},
		};
		const response = await fetch('/api/subscriptions/payment_intent', {
			method: 'POST',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(body),
		});
		const { subscription: data, error: validationError } =
			await response.json();

		if (validationError) {
			showError(validationError);
		} else if (
			data &&
			data.payment_intent &&
			data.payment_intent.client_secret
		) {
			const { paymentIntent, error: paymentError } =
				await stripe.handleCardPayment(
					data.payment_intent.client_secret,
					{ setup_future_usage: 'off_session' }
				);

			if (paymentError) {
				showError(paymentError.message);
				fetch(`/api/subscriptions/${data.id}`, {
					method: 'DELETE',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({
						payment_method_id: paymentError.payment_method.id,
					}),
				});
			} else {
				fetch(`/api/subscriptions/resolve_payment_intent/`, {
					method: 'POST',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({
						subscription: { id: data.id, status: 'succeeded' },
					}),
				});
				location.href = '/subscriptions/resolved';
			}
		} else {
			showError(
				'Payment failed! Please try again or use different card.'
			);
		}
		setIsSubmitting(false);
	};

	const update = async () => {
		setIsSubmitting(true);
		const response = await fetch(
			`/api/subscriptions/${currentSubscription.id}`,
			{
				method: 'PUT',
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(subscription),
			}
		);
		if (response.status < 400) {
			location.href = '/subscriptions/resolved';
		}
	};

	const subscribe = async () => {
		setIsSubmitting(true);

		let paymentMethodId = '';
		if (useDefaultPaymentMethod) {
			paymentMethodId = defaultPaymentMethod.id;
		} else {
			const { paymentMethod, error: paymentMethodError } =
				await stripe.createPaymentMethod('card', {
					billing_details: { name },
				});
			if (paymentMethodError) {
				showError(paymentMethodError.message);
				setIsSubmitting(false);
				return;
			}
			paymentMethodId = paymentMethod.id;
		}

		// const lm = LinkMink('pub_live_ABwf3D5juyYVuDGCAynd');
		// const lmData = lm.getReferralData();

		const body = {
			subscription: {
				...subscription,
				payment_method_id: paymentMethodId,
				lm_data: {}, // TODO: Clean up backend 
			},
		};
		const response = await fetch('/api/subscriptions', {
			method: 'POST',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(body),
		});

		if (response.status < 400) {
			location.href = '/subscriptions/resolved';
		} else {
			const { subscription: data, error: validationError } =
				await response.json();
			if (validationError) {
				showError(validationError);
			} else if (
				data &&
				data.payment_intent &&
				data.payment_intent.client_secret
			) {
				const { paymentIntent, error: paymentError } =
					await stripe.handleCardPayment(
						data.payment_intent.client_secret,
						{ setup_future_usage: 'off_session' }
					);

				if (paymentError) {
					showError(paymentError.message);
					fetch(`/api/subscriptions/${data.id}`, {
						method: 'DELETE',
						headers: {
							Accept: 'application/json',
							'Content-Type': 'application/json',
						},
					});
				} else {
					fetch(`/api/subscriptions/${data.id}`, {
						method: 'PUT',
						headers: {
							Accept: 'application/json',
							'Content-Type': 'application/json',
						},
						body: JSON.stringify({
							subscription: { status: 'active' },
						}),
					});
					location.href = '/subscriptions/resolved';
				}
			} else if (
				data &&
				data.setup_intent &&
				data.setup_intent.client_secret
			) {
				const { setupIntent, error: setupError } =
					await stripe.handleCardSetup(
						data.setup_intent.client_secret,
						{
							payment_method_data: { billing_details: { name } },
						}
					);
				if (setupError) {
					showError(setupError.message);
					fetch(`/api/subscriptions/${data.id}`, {
						method: 'DELETE',
						headers: {
							Accept: 'application/json',
							'Content-Type': 'application/json',
						},
					});
				} else {
					fetch(`/api/subscriptions/${data.id}`, {
						method: 'PUT',
						headers: {
							Accept: 'application/json',
							'Content-Type': 'application/json',
						},
						body: JSON.stringify({
							subscription: { status: 'trialing' },
						}),
					});
					location.href = '/subscriptions/resolved';
				}
			} else {
				showError(
					'Payment failed! Please try again or use different card.'
				);
			}
			setIsSubmitting(false);
		}
	};

	return (
		<React.Fragment>
			<form>
				<div id="subscriptions" className="row">
					<div className="columns medium-6">
						<div style={{ display: 'flex' }}>
							<div style={{ flexGrow: 1 }}>
								<p>
									<b>Payment method</b>
								</p>
							</div>
							{defaultPaymentMethod && (
								<div style={{ display: 'flex' }}>
									<div>Use saved credit card</div>
									<div
										className="switch round small"
										style={{
											marginLeft: '10px',
											marginBottom: '-5px',
										}}
									>
										<input
											id="default-payment-method-switch"
											type="checkbox"
											checked={useDefaultPaymentMethod}
											onChange={
												handleDefaultPaymentMethodChange
											}
										/>
										<label htmlFor="default-payment-method-switch">
											Use Default
										</label>
									</div>
								</div>
							)}
						</div>

						{!useDefaultPaymentMethod && (
							<div className="card">
								<p>Enter new credit card information</p>
								<div className="form-inputs hidden">
									<div className="input-wrapper">
										<label>
											<b>Name on card</b>
											<input
												value={name}
												onChange={(e) =>
													setName(e.target.value)
												}
												placeholder="John Doe"
											/>
										</label>
									</div>
									<div className="input-wrapper">
										<label>
											<b>Card number</b>
											<CardNumberElement
												style={inputStyle}
											/>
										</label>
									</div>
									<div className="input-wrapper">
										<label>
											<b>Expiration date</b>
											<CardExpiryElement
												style={inputStyle}
											/>
										</label>
									</div>
									<div className="input-wrapper">
										<label>
											<b>CVC</b>
											<CardCvcElement
												style={inputStyle}
											/>
										</label>
									</div>
								</div>
							</div>
						)}
					</div>
					<div className="columns medium-6">
						<CheckoutForm
							plan={plan}
							coupon_id={coupon_id}
							handleCouponChange={handleCouponChange}
							handlePlanChange={handlePlanChange}
							submit={submit}
							isSubmitting={isSubmitting}
							product={product}
							plans={plans}
							scTrialAvailable={scTrialAvailable}
							subscription={subscription}
						/>
					</div>
				</div>
			</form>
			<AlertModal onClose={() => setAlertMessage(null)}>
				{alertMessage}
			</AlertModal>
			<LoaderModal isOpen={isSubmitting} />
		</React.Fragment>
	);
};

export default injectStripe(SubscriptionForm);
